'use client';

import dynamic from 'next/dynamic';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import React, { FC, useMemo, useState, useEffect, useCallback } from 'react';

import Modal from '@mui/material/Modal';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Box, Stack, Button, TextField, Container, Typography, Autocomplete } from '@mui/material';

import Pin from './view/pin';
import BookingForm from '../booking-form';
import { useLocales } from '../../../../../locales';
import Image from '../../../../../components/image';
import Iconify from '../../../../../components/iconify';
import { useGetMerchants } from '../../../../../api/shop';
import { MAPBOX_API } from '../../../../../config-global';
import Scrollbar from '../../../../../components/scrollbar';
import { useBoolean } from '../../../../../hooks/use-boolean';
import { BasicPageParams } from '../../../../../types/common';
import FormProvider from '../../../../../components/hook-form';
import { BlockType } from '../../../../../types/page-generator';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { uploadProductsImage } from '../../../../../helpers/uploadProductsImage';

// Отложенная загрузка компонентов карты
const Map = dynamic(() => import('react-map-gl').then((mod) => mod.Map), { ssr: false });
const Marker = dynamic(() => import('react-map-gl').then((mod) => mod.Marker), { ssr: false });
const NavigationControl = dynamic(
  () => import('react-map-gl').then((mod) => mod.NavigationControl),
  { ssr: false }
);

const MAPBOX_TOKEN = MAPBOX_API;

const MerchantMap: FC<{ block?: BlockType; params?: BasicPageParams | any }> = ({
  block,
  params,
}) => {
  const router = useRouter();
  const { merchants } = useGetMerchants({});
  const { currentLang } = useLocales();
  const scrollName: string = block?.scrollName as string;
  const isMobile = useResponsive('down', 'sm');
  const methods = useForm();
  const { handleSubmit } = methods;
  const maxHeight = 100;
  const reservModal = useBoolean();
  const [searchVariant, setSearchVariant] = useState('merchant');
  const [showDescription, setShowDescription] = useState(false);
  const [webGLSupported, setWebGLSupported] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [viewport, setViewport] = useState({
    longitude: 34.781806,
    latitude: 32.0853,
    zoom: 12,
  });

  const shopLink: any =
    block?.shopLink || (`/${params?.params?.lng}/merchant/${popupInfo?.id}` as string);
  const saleLink: any =
    block?.saleLink || (`/${params?.params?.lng}/merchant/${popupInfo?.id}` as string);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    const supported =
      !!window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    setWebGLSupported(!!supported);
  }, []);

  const filteredMerchants = useMemo(
    () =>
      merchants.filter(
        (m: any) => m.latitude && m.longitude && m.latitude !== 0 && m.longitude !== 0
      ),
    [merchants]
  );

  const handleSearch = useCallback(() => {
    // console.log('Search for:', searchQuery);
  }, [searchQuery]);

  const handleInputChange = useCallback(async (_: any, value: string) => {
    //   setSearchQuery(value);
    //   if (!value) return setSuggestions([]);
    //
    //   try {
    //     const response = await axios.get(
    //       `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json`,
    //       {
    //         params: {
    //           access_token: MAPBOX_TOKEN,
    //           autocomplete: true,
    //           limit: 20,
    //         },
    //       }
    //     );
    //     const places = response.data.features.map((f: any) => f.place_name);
    //     setSuggestions(places);
    //   } catch (error) {
    //     console.error('Error fetching suggestions:', error);
    //   }
  }, []);

  const handleMerchantSelect = useCallback((_: any, value: any) => {
    if (!value) return;

    setPopupInfo(value);
    setViewport((prev) => ({
      ...prev,
      longitude: value.longitude,
      latitude: value.latitude,
      zoom: 14,
    }));
  }, []);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setSearchVariant(newAlignment);
  };

  const mapStyle = 'mapbox://styles/mapbox/outdoors-v12';

  const style = {
    position: 'absolute',
    top: 0,
    zIndex: 1,
    width: isMobile ? 200 : 300,
    p: 2,
    border: '1px solid #ccc',
    // borderRadius: 2,
    boxShadow: 3,
    backgroundColor: 'white',
    height: isMobile ? 400 : 700,
    overflowY: 'auto',
  };

  if (typeof window === 'undefined' || !webGLSupported) return null;

  return (
    <Container
      // @ts-ignore
      maxWidth={block?.settings?.fullSize ? 'xxl' : 'xl'}
      sx={{
        mt: 4,
        background: 'white',
        p: { xs: 1, md: 2 },
        borderRadius: '10px',
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(() => {})}>
        <Stack spacing={2}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Map</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <ToggleButtonGroup
                  color="primary"
                  value={searchVariant}
                  exclusive
                  onChange={handleChange}
                  aria-label="Platform"
                >
                  <ToggleButton value="product">Product</ToggleButton>
                  <ToggleButton value="merchant">Merchant</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
          </Stack>
          <>
            {searchVariant === 'merchant' ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <Autocomplete
                  fullWidth
                  options={filteredMerchants}
                  getOptionLabel={(option: any) => option.title || ''}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Enter the name of merchant"
                      variant="outlined"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />
                  )}
                  onChange={(event, value: any) => {
                    if (value) {
                      setSearchQuery(value.title);
                      setPopupInfo(value);
                      setViewport({
                        latitude: value.latitude,
                        longitude: value.longitude,
                        zoom: 14,
                      });
                    }
                  }}
                />
                <Button variant="contained" onClick={handleSearch} sx={{ borderRadius: '10px' }}>
                  Search
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={1}>
                <Autocomplete
                  fullWidth
                  options={[]}
                  getOptionLabel={(option: any) => option.title || ''}
                  renderInput={(p) => (
                    <TextField {...p} label="Enter the name of product" variant="outlined" />
                  )}
                />
                <Button variant="contained" onClick={handleSearch} sx={{ borderRadius: '10px' }}>
                  Search
                </Button>
              </Stack>
            )}
          </>

          <Box
            sx={{
              position: 'relative',
              height: isMobile ? 500 : 700,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Map
              // @ts-ignore
              viewState={viewport}
              mapboxAccessToken={MAPBOX_TOKEN}
              mapStyle={mapStyle}
              onMove={(evt) => setViewport(evt.viewState)}
              reuseMaps
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-left" />
              {filteredMerchants.map((m: any) => (
                <Marker
                  key={m.id}
                  longitude={m.longitude}
                  latitude={m.latitude}
                  anchor="bottom"
                  onClick={() => setPopupInfo(m)}
                >
                  <Box sx={{ cursor: 'pointer' }}>
                    <Pin size={16} />
                  </Box>
                </Marker>
              ))}
            </Map>
            {isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  bgcolor: 'white',
                  px: 2,
                  py: 1,
                  borderTop: '1px solid #ccc',
                  overflowX: 'auto',
                  display: 'flex',
                  gap: 2,
                  height: '150px',
                }}
              >
                {filteredMerchants.map((merchant: any) => (
                  <Box
                    key={merchant.id}
                    sx={{
                      minWidth: 220,
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      boxShadow: 1,
                      pt: 1,
                      px: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Image
                      src={uploadProductsImage(merchant.mainImageURL)}
                      sx={{
                        width: '100%',
                        height: 50,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                    <Typography variant="subtitle2" noWrap>
                      {merchant.title}
                    </Typography>
                    {merchant.phone && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {merchant.phone}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, width: '100%', mb: 1 }}
                      onClick={() => {
                        setPopupInfo(merchant);
                        setViewport({
                          latitude: merchant.latitude,
                          longitude: merchant.longitude,
                          zoom: 14,
                        });
                      }}
                    >
                      Info
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            {!isMobile && (
              <Box
                sx={
                  currentLang.value === 'he'
                    ? {
                        right: 0,
                        ...style,
                      }
                    : {
                        left: 0,
                        ...style,
                      }
                }
              >
                <Stack spacing={2} mt={4}>
                  {filteredMerchants.map((merch: any) => (
                    <Box
                      key={merch.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2,
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        padding: 2,
                        boxShadow: 1,
                      }}
                    >
                      <Image
                        src={uploadProductsImage(merch.mainImageURL)}
                        sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1 }}
                      />
                      <Stack spacing={0.5}>
                        <Typography variant="h6">{merch.title}</Typography>
                        {merch.phone && <Typography variant="body2">{merch.phone}</Typography>}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setPopupInfo(merch);
                            setViewport({
                              latitude: merch.latitude,
                              longitude: merch.longitude,
                              zoom: 14, // можно изменить по желанию
                            });
                          }}
                        >
                          Info
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {popupInfo && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: currentLang.value === 'en' ? 0 : 'auto',
                  right: currentLang.value !== 'en' ? 0 : 'auto',
                  zIndex: 1000,
                  width: isMobile ? 300 : 300,
                  p: 2,
                  border: '1px solid #ccc',
                  // borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: 'white',
                  height: isMobile ? '100%' : 700,
                  overflowY: 'auto',
                }}
              >
                <Stack spacing={1}>
                  <Stack alignItems={currentLang.value === 'en' ? 'flex-end' : 'flex-start'}>
                    <Iconify
                      icon="material-symbols:close-rounded"
                      onClick={() => setPopupInfo(null)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Stack>
                  <Image
                    src={uploadProductsImage(popupInfo.mainImageURL)}
                    sx={{
                      width: '100%',
                      height: isMobile ? '30px' : '150px',
                      objectFit: 'cover',
                    }}
                  />
                  <Typography variant="h6">{popupInfo.title}</Typography>
                  {popupInfo.phone && <Typography variant="h6">{popupInfo.phone}</Typography>}
                  {popupInfo.country && popupInfo.city && popupInfo.street && popupInfo.house && (
                    <Typography>
                      {popupInfo.country} {popupInfo.city} {popupInfo.street} {popupInfo.house}
                    </Typography>
                  )}
                  <>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        textAlign: 'justify',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: showDescription ? 'unset' : 3,
                        WebkitBoxOrient: 'vertical',
                        maxHeight: showDescription ? 'none' : `${maxHeight}px`,
                      }}
                    >
                      {popupInfo.description}
                    </Typography>
                    {popupInfo?.description?.length > 100 && (
                      <Button
                        size="small"
                        onClick={() => setShowDescription((prev) => !prev)}
                        sx={{ alignSelf: 'flex-start', padding: 0, minWidth: 'auto' }}
                        endIcon={
                          showDescription ? (
                            <Iconify icon="ep:arrow-up" />
                          ) : (
                            <Iconify icon="ep:arrow-down" />
                          )
                        }
                      >
                        {showDescription ? 'Hide' : 'Show more'}
                      </Button>
                    )}
                  </>
                  <Typography variant="h6">{scrollName || 'Attributes'}</Typography>

                  {!!popupInfo.attributeValues.length && (
                    <Scrollbar sx={{ maxHeight: isMobile ? 100 : 200 }}>
                      <Stack spacing={0.5}>
                        {popupInfo.attributeValues.map((attribute: any, index: number) => (
                          <Typography sx={{ fontSize: '12px' }} key={index}>
                            {attribute.value}
                          </Typography>
                        ))}
                      </Stack>
                    </Scrollbar>
                  )}
                </Stack>
                {block?.settings?.enableShop &&
                  block?.settings?.enableSale &&
                  block?.settings?.enableReserv && (
                    <Stack
                      width={1}
                      direction={isMobile ? 'column' : 'row'}
                      alignItems="center"
                      gap={1}
                      mt={1}
                      // sx={{ position: 'absolute', bottom: '25px', mt: isMobile ? '252px' : 0   }}
                    >
                      {block?.settings?.enableShop && (
                        <Button
                          variant="outlined"
                          sx={{
                            color: 'black',
                            border: '1px solid black',
                            borderRadius: '10px',
                            width: '100%',
                          }}
                          onClick={() => router.push(shopLink)}
                        >
                          Shop
                        </Button>
                      )}
                      {block?.settings?.enableSale && (
                        <Button
                          variant="outlined"
                          sx={{
                            color: 'red',
                            border: '1px solid red',
                            borderRadius: '10px',
                            width: '100%',
                          }}
                          onClick={() => router.push(saleLink)}
                        >
                          Sale
                        </Button>
                      )}
                      {block?.settings?.enableReserv && (
                        <Button
                          variant="outlined"
                          sx={{
                            color: 'green',
                            border: '1px solid green',
                            borderRadius: '10px',
                            width: '100%',
                          }}
                          onClick={() => reservModal.onTrue()}
                        >
                          Reserv
                        </Button>
                      )}
                    </Stack>
                  )}
              </Box>
            )}
          </Box>
        </Stack>
      </FormProvider>
      <Modal open={reservModal.value} onClose={reservModal.onFalse}>
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: !isMobile ? '700px' : '100%',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            boxShadow: 24,
            p: 4,
          }}
        >
          <BookingForm merchant={popupInfo} close={() => reservModal.onFalse()} />
        </Box>
      </Modal>
    </Container>
  );
};

export default MerchantMap;
